<template>
  <BContainer
    fluid
    class="shared-dashboard py-3 pb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      :title="greetingTitle"
      :subtitle="greetingSubtitle"
      icon="fas fa-chart-line"
      gradient="green"
    />

    <BRow class="g-3 mb-4">
      <BCol
        v-for="card in statCards"
        :key="card.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <div class="kpi-card h-100">
          <div
            class="kpi-icon"
            :style="{ background: card.bg, color: card.color }"
          >
            <i :class="card.icon" />
          </div>
          <div>
            <h4 class="mb-0">
              {{ card.value }}
            </h4>
            <small>{{ card.label }}</small>
          </div>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Completion Rate
          </p>
          <h5 class="mb-0">
            {{ qualityStats.completionRate }}%
          </h5>
          <small>{{ qualityStats.completedCount }} completed appointments</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Active Queue
          </p>
          <h5 class="mb-0">
            {{ qualityStats.activeQueue }}
          </h5>
          <small>for confirmation + scheduled</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Canceled Rate
          </p>
          <h5 class="mb-0">
            {{ qualityStats.canceledRate }}%
          </h5>
          <small>{{ qualityStats.canceledCount }} canceled appointments</small>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="8"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Appointment Volume Trend
              </h6>
              <small class="text-muted">Last 6 months</small>
            </div>
            <apexchart
              type="area"
              height="290"
              :options="appointmentTrendOptions"
              :series="appointmentTrendSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
      <BCol
        cols="12"
        lg="4"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Appointment Status Mix
              </h6>
              <small class="text-muted">Current records</small>
            </div>
            <apexchart
              type="donut"
              height="290"
              :options="appointmentStatusOptions"
              :series="appointmentStatusSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="5"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Cases Queue Snapshot
              </h6>
              <small class="text-muted">Lost and complaint records</small>
            </div>
            <apexchart
              type="bar"
              height="275"
              :options="queueBarOptions"
              :series="queueBarSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
      <BCol
        cols="12"
        lg="7"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Recent Appointments
              </h6>
              <RouterLink
                :to="{ name: 'admin.appointments' }"
                class="small fw-bold"
              >
                Open module
              </RouterLink>
            </div>

            <div
              v-if="recentAppointments.length"
              class="d-flex flex-column gap-2"
            >
              <div
                v-for="appointment in recentAppointments"
                :key="appointment.id"
                class="list-row"
              >
                <div>
                  <p class="mb-0 fw-bold small">
                    {{ appointment.full_name || 'N/A' }}
                  </p>
                  <small class="text-muted">
                    {{ appointmentDateLabel(appointment) }}
                  </small>
                </div>
                <BBadge :variant="statusVariant(appointment.status)">
                  {{ appointment.status || 'unknown' }}
                </BBadge>
              </div>
            </div>
            <div
              v-else
              class="empty-state"
            >
              No recent appointments.
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BRow class="g-3">
      <BCol
        cols="12"
        lg="6"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <h6 class="fw-bold mb-3">
              Quick Actions
            </h6>
            <div class="d-grid gap-2 quick-actions">
              <RouterLink
                :to="{ name: 'admin.users' }"
                class="btn btn-light text-start"
              >
                <i class="fas fa-user-plus me-2 text-success" /> Manage Users
              </RouterLink>
              <RouterLink
                :to="{ name: 'admin.students' }"
                class="btn btn-light text-start"
              >
                <i class="fas fa-user-graduate me-2 text-primary" /> Student Records
              </RouterLink>
              <RouterLink
                :to="{ name: 'admin.lost-and-found' }"
                class="btn btn-light text-start"
              >
                <i class="fas fa-search-location me-2 text-warning" /> Lost and Found
              </RouterLink>
              <RouterLink
                :to="{ name: 'admin.complaints' }"
                class="btn btn-light text-start"
              >
                <i class="fas fa-file-alt me-2 text-danger" /> Complaints
              </RouterLink>
              <RouterLink
                v-if="isSuperuser"
                :to="{ name: 'admin.activity-logs' }"
                class="btn btn-light text-start"
              >
                <i class="fas fa-history me-2 text-info" /> Activity Logs
              </RouterLink>
            </div>
          </BCardBody>
        </BCard>
      </BCol>

      <BCol
        v-if="isSuperuser"
        cols="12"
        lg="6"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Recent Activity Logs
              </h6>
              <RouterLink
                :to="{ name: 'admin.activity-logs' }"
                class="small fw-bold"
              >
                View all
              </RouterLink>
            </div>

            <div
              v-if="recentActivityLogs.length"
              class="d-flex flex-column gap-2"
            >
              <div
                v-for="log in recentActivityLogs"
                :key="log.id"
                class="list-row"
              >
                <div>
                  <p class="mb-0 fw-bold small">
                    {{ log.model || 'Unknown model' }}
                  </p>
                  <small class="text-muted">{{ formatTimestamp(log.timestamp) }}</small>
                </div>
                <BBadge :variant="eventVariant(log.event)">
                  {{ log.event || 'event' }}
                </BBadge>
              </div>
            </div>
            <div
              v-else
              class="empty-state"
            >
              No recent activity.
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import { SAdminUser, SAdminAppointment, SAdminLostItem, SAdminStudent, SAdminComplaint, SAdminActivityLog } from '@/services'
import { SHARED_DASHBOARD_ROLE_CODES } from '@/config/roles'
import AdminPageHeader from '@/components/core/AdminPageHeader'

const APPOINTMENT_STATUSES = ['for_confirmation', 'scheduled', 'completed', 'canceled']

export default {
  name: 'Dashboard',

  components: {
    AdminPageHeader
  },

  middleware: ['auth'],

  metaInfo: () => ({
    title: 'Dashboard'
  }),

  data () {
    return {
      stats: {
        users: 0,
        appointments: 0,
        lostFound: 0,
        students: 0,
        complaints: 0
      },
      recentAppointments: [],
      recentActivityLogs: [],
      qualityStats: {
        completionRate: 0,
        canceledRate: 0,
        activeQueue: 0,
        completedCount: 0,
        canceledCount: 0
      },
      manilaNow: new Date(),
      timeTicker: null,
      isDarkMode: false,
      themeObserver: null,
      appointmentStatusSeries: [0, 0, 0, 0],
      appointmentTrendSeries: [{
        name: 'Appointments',
        data: [0, 0, 0, 0, 0, 0]
      }],
      queueBarSeries: [{
        name: 'Count',
        data: [0, 0]
      }]
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    isAdminRole () {
      return SHARED_DASHBOARD_ROLE_CODES.includes(this.currentUser?.role)
    },

    isSuperuser () {
      return this.currentUser?.role === 'superuser'
    },

    userDisplayName () {
      if (this.currentUser?.first_name) {
        return this.currentUser.first_name
      }

      if (this.currentUser?.name) {
        return this.currentUser.name
      }

      return 'User'
    },

    manilaHour () {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Manila',
        hour: 'numeric',
        hour12: false
      })

      const hourRaw = formatter.format(this.manilaNow)
      const parsedHour = Number(hourRaw)

      return Number.isNaN(parsedHour) ? 12 : parsedHour
    },

    greetingTitle () {
      const hour = this.manilaHour

      if (hour >= 5 && hour < 12) {
        return `Good morning, ${this.userDisplayName}`
      }

      if (hour >= 12 && hour < 18) {
        return `Good afternoon, ${this.userDisplayName}`
      }

      return `Good evening, ${this.userDisplayName}`
    },

    greetingSubtitle () {
      const dateLabel = new Intl.DateTimeFormat('en-PH', {
        timeZone: 'Asia/Manila',
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }).format(this.manilaNow)

      const timeLabel = new Intl.DateTimeFormat('en-PH', {
        timeZone: 'Asia/Manila',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(this.manilaNow)

      return `${dateLabel} • ${timeLabel} (Manila time)`
    },

    statCards () {
      return [
        { label: 'Users', value: this.stats.users, icon: 'fas fa-users', color: '#0f766e', bg: 'rgba(20, 184, 166, 0.16)' },
        { label: 'Appointments', value: this.stats.appointments, icon: 'fas fa-calendar-check', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.16)' },
        { label: 'Lost Items', value: this.stats.lostFound, icon: 'fas fa-search-location', color: '#b45309', bg: 'rgba(245, 158, 11, 0.16)' },
        { label: 'Students', value: this.stats.students, icon: 'fas fa-user-graduate', color: '#6d28d9', bg: 'rgba(139, 92, 246, 0.16)' }
      ]
    },

    appointmentTrendOptions () {
      return {
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3 },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.35,
            opacityFrom: 0.45,
            opacityTo: 0.06,
            stops: [0, 95, 100]
          }
        },
        xaxis: {
          categories: this.lastMonthLabels(6),
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        colors: ['#7ab6f9'],
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    },

    appointmentStatusOptions () {
      return {
        labels: ['For Confirmation', 'Scheduled', 'Completed', 'Canceled'],
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        legend: { position: 'bottom' },
        colors: ['#f9cfa7', '#a7c8f9', '#b8e3c2', '#f7b4bf'],
        dataLabels: {
          formatter: value => `${Math.round(value)}%`
        },
        plotOptions: {
          pie: {
            donut: {
              size: '60%'
            }
          }
        }
      }
    },

    queueBarOptions () {
      return {
        chart: { toolbar: { show: false } },
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: '45%'
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: ['Lost and Found', 'Complaints'],
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        colors: ['#f6c5a5'],
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    }
  },

  mounted () {
    this.startManilaTicker()
    this.syncColorMode()
    this.setupColorModeObserver()

    if (this.isAdminRole) {
      this.fetchDashboardMetrics()
    }
  },

  beforeDestroy () {
    this.stopManilaTicker()

    if (this.themeObserver) {
      this.themeObserver.disconnect()
      this.themeObserver = null
    }
  },

  methods: {
    statusVariant (status) {
      const variants = {
        for_confirmation: 'warning',
        scheduled: 'primary',
        completed: 'success',
        canceled: 'danger'
      }

      return variants[status] || 'secondary'
    },

    eventVariant (event) {
      const variants = {
        created: 'success',
        updated: 'warning',
        deleted: 'danger'
      }

      return variants[event] || 'secondary'
    },

    formatTimestamp (value) {
      if (!value) return 'N/A'

      const dt = new Date(value)
      return Number.isNaN(dt.getTime()) ? 'N/A' : dt.toLocaleString()
    },

    appointmentDateLabel (appointment) {
      const date = appointment?.appointment_slot?.date || appointment?.created_at
      const time = appointment?.appointment_slot?.start_time

      if (!date) {
        return 'No schedule available'
      }

      return time ? `${date} ${time}` : date
    },

    monthKey (value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return null
      }

      const month = `${date.getMonth() + 1}`.padStart(2, '0')
      return `${date.getFullYear()}-${month}`
    },

    lastMonthLabels (count) {
      const result = []
      const dt = new Date()

      for (let index = count - 1; index >= 0; index--) {
        const cursor = new Date(dt.getFullYear(), dt.getMonth() - index, 1)
        result.push(cursor.toLocaleString('default', { month: 'short' }))
      }

      return result
    },

    async fetchDashboardMetrics () {
      const param = { page: 1, per_page: 250 }

      const [users, appointments, lfItems, students, complaints] = await Promise.all([
        SAdminUser.getList({ page: 1, per_page: 1 }).catch(() => ({ data: { total_rows: 0 } })),
        SAdminAppointment.getList(param).catch(() => ({ data: { total_rows: 0, items: [] } })),
        SAdminLostItem.getList(param).catch(() => ({ data: { total_rows: 0, items: [] } })),
        SAdminStudent.getList({ page: 1, per_page: 1 }).catch(() => ({ data: { total_rows: 0 } })),
        SAdminComplaint.getList(param).catch(() => ({ data: { total_rows: 0, items: [] } }))
      ])

      const appointmentItems = appointments?.data?.items || []

      this.stats.users = users?.data?.total_rows || 0
      this.stats.appointments = appointments?.data?.total_rows || appointmentItems.length
      this.stats.lostFound = lfItems?.data?.total_rows || 0
      this.stats.students = students?.data?.total_rows || 0
      this.stats.complaints = complaints?.data?.total_rows || 0
      this.recentAppointments = appointmentItems.slice(0, 5)

      const completedCount = appointmentItems.filter(item => item.status === 'completed').length
      const canceledCount = appointmentItems.filter(item => item.status === 'canceled').length
      const activeQueue = appointmentItems.filter(item => ['for_confirmation', 'scheduled'].includes(item.status)).length
      const denominator = appointmentItems.length || 1

      this.qualityStats = {
        completionRate: Math.round((completedCount / denominator) * 100),
        canceledRate: Math.round((canceledCount / denominator) * 100),
        activeQueue,
        completedCount,
        canceledCount
      }

      this.buildAppointmentStatusSeries(appointmentItems)
      this.buildAppointmentTrendSeries(appointmentItems)

      this.queueBarSeries = [{
        name: 'Count',
        data: [this.stats.lostFound, this.stats.complaints]
      }]

      if (this.isSuperuser) {
        this.fetchRecentActivityLogs()
      }
    },

    buildAppointmentStatusSeries (appointments) {
      const counts = APPOINTMENT_STATUSES.reduce((acc, status) => {
        acc[status] = 0
        return acc
      }, {})

      appointments.forEach(appointment => {
        if (counts[appointment.status] !== undefined) {
          counts[appointment.status] += 1
        }
      })

      this.appointmentStatusSeries = APPOINTMENT_STATUSES.map(status => counts[status])
    },

    buildAppointmentTrendSeries (appointments) {
      const keys = []
      const now = new Date()

      for (let index = 5; index >= 0; index--) {
        const cursor = new Date(now.getFullYear(), now.getMonth() - index, 1)
        const month = `${cursor.getMonth() + 1}`.padStart(2, '0')
        keys.push(`${cursor.getFullYear()}-${month}`)
      }

      const buckets = keys.reduce((acc, key) => {
        acc[key] = 0
        return acc
      }, {})

      appointments.forEach(appointment => {
        const dateSource = appointment?.appointment_slot?.date || appointment?.created_at
        const key = this.monthKey(dateSource)

        if (key && buckets[key] !== undefined) {
          buckets[key] += 1
        }
      })

      this.appointmentTrendSeries = [{
        name: 'Appointments',
        data: keys.map(key => buckets[key])
      }]
    },

    async fetchRecentActivityLogs () {
      return SAdminActivityLog.getList({ page: 1, per_page: 5, sort: 'created_at', sort_desc: true })
        .then(({ data: response }) => {
          const data = response?.data || response
          this.recentActivityLogs = data?.items || []
        })
        .catch(() => {
          this.recentActivityLogs = []
        })
    },

    syncColorMode () {
      this.isDarkMode = document.body.classList.contains('dark')
    },

    setupColorModeObserver () {
      this.themeObserver = new MutationObserver(() => {
        this.syncColorMode()
      })

      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    },

    startManilaTicker () {
      this.manilaNow = new Date()
      this.timeTicker = window.setInterval(() => {
        this.manilaNow = new Date()
      }, 60000)
    },

    stopManilaTicker () {
      if (this.timeTicker) {
        window.clearInterval(this.timeTicker)
        this.timeTicker = null
      }
    }
  }
}
</script>

<style scoped>
.shared-dashboard {
  --slate-900: #0f172a;
  --slate-700: #334155;
  --slate-400: #94a3b8;
  --panel-border: #e2e8f0;
  background: radial-gradient(circle at 0% 0%, #fdf2f8 0%, #f8fafc 35%, #eef6ff 100%);
}

:global(body.dark) .shared-dashboard {
  --slate-900: #ecf4ff;
  --slate-700: #dbeafe;
  --slate-400: #bfd4f6;
  --panel-border: #334155;
  background: radial-gradient(circle at 0% 0%, #1f2937 0%, #111827 45%, #1e293b 100%);
}

.hero-panel {
  display: flex;
  gap: 0.9rem;
  align-items: center;
  padding: 1.1rem 1.2rem;
  border-radius: 1rem;
  color: #fff;
  background: linear-gradient(110deg, #0f766e, #0ea5e9 60%, #1d4ed8);
  box-shadow: 0 18px 32px rgba(14, 116, 144, 0.24);
}

.hero-badge {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 0.8rem;
  background: rgba(255, 255, 255, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
}

.hero-panel h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
}

.hero-panel p {
  opacity: 0.9;
  font-size: 0.85rem;
}

.kpi-card {
  background: #fff;
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem;
}

:global(body.dark) .shared-dashboard .kpi-card,
:global(body.dark) .shared-dashboard .dash-card,
:global(body.dark) .shared-dashboard .detail-card {
  background: #1e293b;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-card h4 {
  color: var(--slate-900);
  font-size: 1.25rem;
  font-weight: 700;
}

.kpi-card small {
  color: var(--slate-400);
}

.dash-card {
  border: 1px solid var(--panel-border);
  border-radius: 1rem;
  box-shadow: 0 12px 24px rgba(30, 41, 59, 0.04);
}

:global(body.dark) .shared-dashboard .dash-card {
  box-shadow: 0 14px 24px rgba(2, 6, 23, 0.35);
}

.list-row {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

:global(body.dark) .shared-dashboard .list-row {
  border-color: #475569;
  background: rgba(148, 163, 184, 0.08);
}

.empty-state {
  color: #94a3b8;
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  text-align: center;
  padding: 1rem;
}

:global(body.dark) .shared-dashboard .empty-state {
  border-color: #64748b;
  color: #cbd5e1;
}

.quick-actions .btn {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
}

:global(body.dark) .shared-dashboard .quick-actions .btn {
  background: #223149;
  border-color: #475569;
  color: #e2e8f0;
}

.detail-card {
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
  padding: 0.85rem 0.95rem;
}

.detail-card h5 {
  color: var(--slate-900);
  font-weight: 700;
}

.detail-card small {
  color: var(--slate-400);
}

.detail-label {
  color: var(--slate-700);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

@media (min-width: 992px) {
  .hero-panel h2 {
    font-size: 1.45rem;
  }

  .hero-panel p {
    font-size: 0.92rem;
  }
}
</style>
